import useModal from "@/hooks/useModal";
import { Controller, useForm } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import DateSelect from "@/components/DateSelect";
import { useSearch } from "@/hooks/useSearch";
import { useQuery } from "@apollo/client";
import { Control, useController, useFormContext } from "react-hook-form";
import { useMemo, useEffect, useRef } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import Button from "@/components/Button";
import { UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { GET_PDR_ACTIVITY_CODES_QUERY } from "../../queries";
import { FieldPathByValue } from "react-hook-form";
import { ControlledTextField as TextField } from "@/components/TextField";
import LocationAutocompleteInput from "./LocationAutocompleteInput";
import { DateTime } from "luxon";
import { sortBy } from "lodash";
import { excludedActivityCodes } from "../_utils";
import Banner from "@/components/Banner";

export type FieldValues = {
  activityCode: string;
  activityType: string;
  person: string;
  personId: string | null;
  title: string;
  activityDescription: string;
  totalHours: string;
  creditsRequested: string;
  creditsAwarded: string;
  publishedAt: string;
  startDate: string;
  endDate: string;
  location: string;
};

function AddressForm({ control }: { control: UseFormReturn<FieldValues>["control"] }) {
  return (
    <div className="mt-6 flex flex-col sm:flex-row gap-6 sm:gap-2">
      <div className="flex-grow">
        <LocationAutocompleteInput control={control} name="location" label={`Location (City or Zip)`} />
      </div>
    </div>
  );
}

function SingleDatePicker({
  control,
  label,
  required = false,
  name,
  minDate
}: {
  control: Control<FieldValues>;
  label: string;
  required?: boolean;
  name: FieldPathByValue<FieldValues, string>;
  minDate?: string;
}) {
  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    control,
    name,
    rules: {
      validate: (value) => {
        if (!value) {
          return `${label} is required`;
        }
        if (name === "endDate" && minDate) {
          if (value < minDate) {
            return "End date must be after start date";
          }
        }

        const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
        if (!dateRegex.test(value)) {
          return "Date must be in MM/DD/YYYY format";
        }

        const [year, month, day] = value.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
          return "Please enter a valid date";
        }

        return true;
      }
    }
  });

  const { watch, setValue } = useFormContext<FieldValues>();
  const minDateObj = minDate ? DateTime.fromISO(minDate).startOf("day").toJSDate() : undefined;

  return (
    <DateSelect
      label={required ? `${label} *` : label}
      value={{ startDate: value, endDate: value }}
      useModal={true}
      onChange={(x: DateValueType | string) => {
        if (typeof x === "string") return;

        if (x === null) {
          onChange("");
          return;
        }

        onChange(x.startDate);
      }}
      onBlur={() => {
        if (name === "startDate") {
          const endDate = watch("endDate");
          if (!endDate) {
            setValue("endDate", value);
          }
        }
      }}
      useSingleDate
      useMultiCalendar={false}
      error={!!error}
      errorMessage={error?.message}
      minDate={minDateObj}
      maxDate={new Date()}
    />
  );
}

export default function AddParticipationDialog({
  onDismiss,
  pgaId,
  defaultValues,
  onSubmit,
  activityCode,
  setActivityCode,
  bannerError,
  hasAssociatedActivity
}: {
  onDismiss: () => void;
  pgaId: string;
  defaultValues?: FieldValues;
  onSubmit: (values: FieldValues) => void;
  activityCode: string | null;
  setActivityCode: (activityCode: string | null) => void;
  bannerError: string;
  /** When true, hide Activity Type, dates, and location (participation is linked to an activity) */
  hasAssociatedActivity?: boolean;
}) {
  const formDefaultValues = useMemo(() => {
    const createDefaultValues: FieldValues = {
      activityCode: "",
      activityType: "",
      person: "",
      personId: pgaId || "",
      title: "",
      activityDescription: "",
      totalHours: "",
      creditsRequested: "",
      creditsAwarded: "",
      publishedAt: "",
      startDate: "",
      endDate: "",
      location: ""
    };
    return {
      ...createDefaultValues,
      ...defaultValues
    };
  }, [defaultValues, pgaId]);

  const searchField = "person";
  const { Modal } = useModal();
  const form = useForm<FieldValues>({
    defaultValues: formDefaultValues,
    mode: "onChange"
  });

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, isDirty }
  } = form;

  const hasAppliedEditData = useRef(false);
  useEffect(() => {
    const hasData = !!(defaultValues?.title || defaultValues?.activityCode);
    if (hasData && !hasAppliedEditData.current) {
      reset(formDefaultValues);
      hasAppliedEditData.current = true;
    }
  }, [formDefaultValues, reset, defaultValues]);
  const { searchValue, control: searchControl } = useSearch(searchField);

  const { data: personData, loading: peopleLoading } = useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: {
        search: searchValue
      }
    }
  });

  const { data: activityCodes } = useQuery(GET_PDR_ACTIVITY_CODES_QUERY, {
    variables: {
      activityCode: ""
    }
  });

  const activityTypes =
    sortBy(activityCodes?.pdrActivityCodes, "description").map(
      (activityCode: { description: string; activityCode: string }) => ({
        displayText: activityCode.description,
        value: activityCode.activityCode
      })
    ) || [];
  console.log(activityCode);
  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button disabled={isSubmitting && !isDirty} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      }
      headline={`${defaultValues ? "Edit" : "Add"} Participation`}
      displayOverflow
      className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto"
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The participation could not be saved." subText={bannerError} />
        </div>
      )}
      <FormProvider {...form}>
        <input className="absolute opacity-0" />
        <div className="space-y-6">
          {!hasAssociatedActivity && (
            <Controller
              control={control}
              name="activityType"
              rules={{
                ...(defaultValues?.activityCode ? { required: false } : { required: "Activity Type is required" })
              }}
              render={({ field: { onChange, value }, fieldState }) => (
                <Autocomplete
                  autofocus={false}
                  label="Activity Type"
                  required
                  disabled={!!defaultValues?.activityCode}
                  query={value}
                  loadingResults={false}
                  data={activityTypes.filter(
                    (activityType: { displayText: string; value: string }) =>
                      activityType.displayText.toLowerCase().includes(value?.toLowerCase() || "") ||
                      activityType.value?.toLowerCase().includes(value?.toLowerCase() || "")
                  )}
                  onChangeText={onChange}
                  reset={() => {
                    onChange("");
                    setActivityCode(null);
                  }}
                  onSelect={(value: { displayText: string; value: string }) => {
                    onChange(`${value.displayText}`);
                    setActivityCode(value.value);
                  }}
                  renderItem={(opt) => (
                    <div className="flex flex-col">
                      <div className="text-body-large">{opt.displayText}</div>
                      <div className="text-label-medium text-sys-brand-on-surface-variant">{opt.value}</div>
                    </div>
                  )}
                  error={fieldState.invalid}
                  errorText={fieldState.error?.message}
                  variant="searchableDropdown"
                  menuClassName="max-w-[300px] max-h-[300px] overflow-y-auto"
                />
              )}
            />
          )}
          <input type="hidden" name="person" />
          {!pgaId && (
            <Controller
              name="person"
              rules={{ required: "Person is required" }}
              control={searchControl}
              render={({ field: { onChange, value }, fieldState }) => {
                return (
                  <Autocomplete
                    autofocus={true}
                    label="Person"
                    required
                    query={value}
                    loadingResults={peopleLoading}
                    data={personData?.people?.nodes || []}
                    onChangeText={onChange}
                    reset={() => {
                      onChange("");
                      setValue("personId", null);
                    }}
                    onSelect={(value) => {
                      onChange(`${value.firstName} ${value.lastName}`);
                      setValue("personId", value.pgaId);
                    }}
                    renderItem={(data: {
                      id: string;
                      firstName: string;
                      lastName: string;
                      pgaId: string;
                      primarySectionAffiliation: { section: { name: string } };
                      primaryFacility: { name: string };
                      profilePhoto: string;
                    }) => (
                      <AvatarDetails
                        title={`${data.firstName} ${data.lastName}`}
                        text={data.primaryFacility?.name || ""}
                        picture={data.profilePhoto}
                        secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                      />
                    )}
                    error={fieldState.invalid}
                    errorText={fieldState.error?.message}
                  />
                );
              }}
            />
          )}
          <TextField className="w-full" control={control} name="title" label="Title" required />
          <TextField
            className="w-full"
            control={control}
            name="activityDescription"
            label="Activity Description"
            type="textarea"
            rules={{
              maxLength: {
                value: 255,
                message: "Must be less than 255 characters"
              }
            }}
          />
          {excludedActivityCodes(activityCode) && (
            <>
              <TextField
                className="w-full"
                control={control}
                name="creditsAwarded"
                label="Credits Awarded"
                type="number"
                required
                onBlur={(e) => {
                  const creditsRequested = watch("creditsRequested");
                  if (!creditsRequested) {
                    setValue("creditsRequested", (e.target as HTMLInputElement).value);
                  }
                }}
              />
              <TextField
                className="w-full"
                control={control}
                name="creditsRequested"
                label="Credits Requested"
                type="number"
                required
                rules={{
                  validate: (value) => {
                    if (Number(value) < Number(watch("creditsAwarded"))) {
                      return "Credits Requested must be at least equal to Credits Awarded";
                    }
                    return true;
                  }
                }}
              />
            </>
          )}

          {activityCode === "M06" && (
            <TextField
              className="w-full"
              control={control}
              name="totalHours"
              label="Total Hours Served"
              required
              rules={{
                required: "Total hours is required",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Must be a number"
                }
              }}
            />
          )}
          {!hasAssociatedActivity &&
            (activityCode === "M09" ? (
              <SingleDatePicker control={control} label="Publish Date" name="publishedAt" required />
            ) : (
              <>
                <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
                  <SingleDatePicker control={control} label="Start Date" name="startDate" required />

                  <SingleDatePicker
                    control={control}
                    label="End Date"
                    name="endDate"
                    required
                    minDate={watch("startDate")}
                  />
                </div>
                <AddressForm control={control} />
              </>
            ))}
        </div>
      </FormProvider>
    </Modal>
  );
}
